<template>
    <div class="w-full pt-12 pb-2">
        <!-- Divider spans the full section width to match the form card above; the inner
             content is padded so it aligns with the form fields (sm:px-6). -->
        <div class="mb-6 flex items-center justify-between gap-4 border-b border-gray-200 pb-4 sm:px-6">
            <h2 class="m-0 p-0 text-lg font-semibold leading-none text-gray-900">
                Alarm Activity
            </h2>
            <button type="button"
                    @click="$emit('show-notifications')"
                    class="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                Show Notification Log
            </button>
        </div>

        <p v-if="orderedComments.length === 0" class="text-base text-gray-500 sm:px-6">
            No activity yet.
        </p>

        <!-- Vertical timeline: a colored dot per event on a shared rail. The rail is offset
             just enough that the event content aligns with the form fields above it. -->
        <ol v-else class="relative border-l border-gray-200 sm:ml-2 sm:pl-4">
            <li v-for="comment in orderedComments" :key="comment.id" class="relative mb-6 last:mb-0">
                <span class="absolute -left-6 ml-px top-2 h-3 w-3 rounded-full ring-4 ring-white"
                      :class="dotClasses(comment)"></span>

                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span :class="badgeClasses(comment)">{{ eventLabel(comment) }}</span>
                    <p class="text-base text-gray-500">
                        <span :class="authorClasses(comment)">
                            {{ comment.user_name }}
                        </span>
                        <span class="text-gray-300"> &middot; </span>{{ comment.date }}
                    </p>
                </div>
                <p v-if="comment.comment"
                   class="mt-2 text-base leading-7 text-gray-600 whitespace-pre-wrap">
                    {{ comment.comment }}
                </p>
            </li>
        </ol>
    </div>
</template>

<script>
    // This is the only place alarm-activity styling is used, so the color maps live inline
    // here rather than in a shared component.
    const BADGE_CLASSES = {
        gray: 'bg-gray-100 text-gray-800',
        red: 'bg-red-100 text-red-800',
        green: 'bg-green-100 text-green-800',
        yellow: 'bg-amber-100 text-amber-800',
        blue: 'bg-blue-100 text-blue-800',
    };

    const DOT_CLASSES = {
        gray: 'bg-gray-300',
        red: 'bg-red-400',
        green: 'bg-green-400',
        yellow: 'bg-amber-400',
        blue: 'bg-blue-400',
    };

    export default {
        name: "alarmComments",
        props: {
            comments: {
                type: Array,
                default: () => [],
            },
        },
        computed: {
            // Newest first. Copy before reversing so we never mutate the prop.
            orderedComments() {
                return this.comments.slice().reverse();
            },
        },
        methods: {
            // Every concrete comment subclass supplies a non-empty comment_type. Only a bare
            // base AlarmComment has an empty type; fall back to a neutral label for it rather
            // than mislabeling it as a specific action.
            eventLabel(comment) {
                return comment.comment_type || 'Activity';
            },
            badgeClasses(comment) {
                const color = BADGE_CLASSES[comment.badge_color] || BADGE_CLASSES.gray;
                return `inline-flex items-center rounded-full px-3 py-0.5 text-base font-medium ${color}`;
            },
            dotClasses(comment) {
                return DOT_CLASSES[comment.badge_color] || DOT_CLASSES.gray;
            },
            // Author styling: deleted human author -> italic placeholder; system -> muted;
            // live human -> bold. user_name is always populated so the ' · ' never dangles.
            authorClasses(comment) {
                if (comment.is_deleted_user) {
                    return 'italic text-gray-400';
                }
                return comment.is_system ? 'text-gray-500' : 'font-medium text-gray-700';
            },
        },
    }
</script>

<style scoped>

</style>
